/** Resolve a tiered_shared laborStructure into a tiered one for engine evaluation. */ export function resolveSharedTiers(laborStructure, sharedConfigs) {
    if (laborStructure.type !== 'tiered_shared') return laborStructure;
    const configMap = new Map(sharedConfigs.map((c)=>[
            c.id,
            c
        ]));
    const tiers = laborStructure.tierRefs.map((ref)=>{
        const shared = configMap.get(ref.sharedTierConfigId);
        if (!shared) {
            // Fallback: return an empty tier
            return {
                tierLabel: 'Unknown',
                tierNumber: 0,
                group: {
                    id: '',
                    label: 'Unknown',
                    groupType: 'tier',
                    lines: []
                },
                fteConstraints: {}
            };
        }
        return {
            tierLabel: shared.tierLabel,
            tierNumber: shared.tierNumber,
            group: structuredClone(shared.group),
            fteConstraints: ref.overrides?.fteConstraints ?? shared.fteConstraints,
            exerciseCount: ref.exerciseCount
        };
    });
    return {
        type: 'tiered',
        tiers
    };
}
/**
 * Client-side PricingEngine — direct port of the backend engine.
 * All methods are pure functions (no I/O). Used for real-time recomputation
 * when the user edits parameters in the pricing workspace.
 */ export class PricingEngine {
    // ── Formula Engine ────────────────────────────────────────────────────
    evaluateFormula(formula, context) {
        switch(formula.type){
            case 'multiply':
                return formula.operands.reduce((acc, op)=>acc * (context[op] ?? 0), 1);
            case 'sum':
                {
                    const prefix = formula.over.replace('.*', '.');
                    let total = 0;
                    for (const [key, val] of Object.entries(context)){
                        if (key.startsWith(prefix) || key.endsWith(`.${formula.over}`)) {
                            total += val;
                        }
                    }
                    return total;
                }
            case 'subtract':
                return (context[formula.left] ?? 0) - (context[formula.right] ?? 0);
            case 'divide':
                {
                    const denom = context[formula.denominator] ?? 0;
                    return denom === 0 ? 0 : (context[formula.numerator] ?? 0) / denom;
                }
            case 'percentage':
                {
                    const base = context[formula.base] ?? 0;
                    return base === 0 ? 0 : ((context[formula.value] ?? 0) - base) / base;
                }
            case 'product_chain':
                return formula.factors.reduce((acc, f)=>acc * (context[f] ?? 0), 1);
            case 'wrap_rate':
                return (context[formula.pools] ?? 1) * (1 + (context[formula.fee] ?? 0));
            case 'escalate':
                {
                    const baseVal = context[formula.base] ?? 0;
                    const rate = context[formula.rate] ?? 0;
                    const year = context[formula.year] ?? 1;
                    return baseVal * (1 + rate) ** (year - 1);
                }
            case 'clamp':
                {
                    const val = context[formula.value] ?? 0;
                    const min = context[formula.min] ?? Number.NEGATIVE_INFINITY;
                    const max = context[formula.max] ?? Number.POSITIVE_INFINITY;
                    return Math.max(min, Math.min(max, val));
                }
            case 'conditional':
                {
                    const cond = context[formula.condition] ?? 0;
                    return cond > 0 ? context[formula.ifTrue] ?? 0 : context[formula.ifFalse] ?? 0;
                }
            case 'lookup':
                return context[`${formula.table}.${formula.key}`] ?? 0;
            case 'custom':
                return 0;
        }
    }
    // ── Line & Group Evaluation ───────────────────────────────────────────
    evaluateLine(line, externalContext, period) {
        const params = line.parameters;
        const resolved = {
            ...externalContext
        };
        const paramNames = Object.keys(params);
        const paramSet = new Set(paramNames);
        const deps = new Map();
        for (const name of paramNames){
            const param = params[name];
            if (param.formula) {
                deps.set(name, this.getFormulaDeps(param.formula));
            } else {
                deps.set(name, []);
            }
        }
        // Topological sort (Kahn's algorithm)
        const inDegree = new Map();
        for (const name of paramNames){
            const paramDeps = deps.get(name) ?? [];
            const internalCount = paramDeps.filter((d)=>paramSet.has(d)).length;
            inDegree.set(name, internalCount);
        }
        const queue = [];
        for (const [name, degree] of inDegree){
            if (degree === 0) queue.push(name);
        }
        const order = [];
        while(queue.length > 0){
            const name = queue.shift();
            if (name == null) break;
            order.push(name);
            for (const [other, otherDeps] of deps){
                if (otherDeps.includes(name)) {
                    const newDeg = (inDegree.get(other) ?? 1) - 1;
                    inDegree.set(other, newDeg);
                    if (newDeg === 0) queue.push(other);
                }
            }
        }
        // If topological sort didn't include all params (cycle), add remaining
        for (const name of paramNames){
            if (!order.includes(name)) order.push(name);
        }
        // Evaluate in topological order
        for (const name of order){
            const param = params[name];
            if (!param) continue;
            if (param.source === 'computed' && param.formula) {
                resolved[name] = this.evaluateFormula(param.formula, resolved);
            } else if (param.perPeriod && period && param.periodValues?.[period] != null) {
                resolved[name] = param.periodValues[period];
            } else if (param.value != null) {
                resolved[name] = param.value;
            } else if (!(name in resolved)) {
                resolved[name] = 0;
            }
        }
        return resolved;
    }
    evaluateGroup(group, externalContext, period, rateLookup) {
        const lineResults = [];
        let subtotal = 0;
        for (const line of group.lines){
            const lineCtx = {
                ...externalContext
            };
            if (rateLookup && line.laborCategoryId && rateLookup[line.laborCategoryId] != null) {
                lineCtx.rate = rateLookup[line.laborCategoryId];
            }
            if (line.fteCount != null && !('ftes' in lineCtx)) {
                lineCtx.ftes = line.fteCount;
            }
            const result = this.evaluateLine(line, lineCtx, period);
            lineResults.push(result);
            subtotal += result.total ?? result.line_total ?? 0;
        }
        let discount = 0;
        if (group.discount) {
            if (group.discount.source === 'computed' && group.discount.formula) {
                discount = this.evaluateFormula(group.discount.formula, {
                    subtotal,
                    ...externalContext
                });
            } else {
                discount = group.discount.value ?? 0;
            }
        }
        const total = subtotal - discount;
        return {
            lineResults,
            subtotal,
            discount,
            total
        };
    }
    // ── Dimension Evaluators ──────────────────────────────────────────────
    applyRateMethod(method, bareRate, categoryId, _period) {
        switch(method.type){
            case 'bare_plus_wrap':
                {
                    const wrapRate = this.computeWrapRateFromConfig(method.wrapRateConfig, 'labor');
                    return bareRate * wrapRate;
                }
            case 'fully_loaded':
                return bareRate;
            case 'vehicle_schedule':
                return bareRate;
            case 'ceiling_constrained':
                {
                    const ceiling = method.ceilingRates[categoryId] ?? Number.POSITIVE_INFINITY;
                    return Math.min(bareRate, ceiling);
                }
            case 'sca_wage':
                return bareRate;
        }
    }
    computeFee(method, totalCost) {
        switch(method.type){
            case 'fixed_percentage':
                {
                    const pct = method.feePercent.value ?? 0;
                    const feeAmount = totalCost * pct;
                    return {
                        feeAmount,
                        totalWithFee: totalCost + feeAmount
                    };
                }
            case 'incentive':
                {
                    const targetCost = method.targetCost.value ?? 0;
                    const targetFee = method.targetFee.value ?? 0;
                    const minFee = method.minFee.value ?? 0;
                    const maxFee = method.maxFee.value ?? 0;
                    const shareUnder = method.shareRatioUnder.value ?? 0;
                    const shareOver = method.shareRatioOver.value ?? 0;
                    const costDelta = targetCost - totalCost;
                    const shareRatio = costDelta >= 0 ? shareUnder : shareOver;
                    const adjustedFee = targetFee + costDelta * shareRatio;
                    const feeAmount = Math.max(minFee, Math.min(maxFee, adjustedFee));
                    return {
                        feeAmount,
                        totalWithFee: totalCost + feeAmount
                    };
                }
            case 'award_fee':
                {
                    const basePct = method.baseFeePercent.value ?? 0;
                    const feeAmount = totalCost * basePct;
                    return {
                        feeAmount,
                        totalWithFee: totalCost + feeAmount
                    };
                }
            case 'incentive_firm':
                {
                    const targetCost = method.targetCost.value ?? 0;
                    const targetProfit = method.targetProfit.value ?? 0;
                    const ceilingPrice = method.ceilingPrice.value ?? 0;
                    const shareRatio = method.shareRatio.value ?? 0;
                    const costDelta = targetCost - totalCost;
                    const adjustedProfit = targetProfit + costDelta * shareRatio;
                    const priceBeforeCeiling = totalCost + adjustedProfit;
                    const price = Math.min(priceBeforeCeiling, ceilingPrice);
                    const feeAmount = price - totalCost;
                    return {
                        feeAmount,
                        totalWithFee: price
                    };
                }
            case 'embedded':
                return {
                    feeAmount: 0,
                    totalWithFee: totalCost
                };
            case 'none':
                return {
                    feeAmount: 0,
                    totalWithFee: totalCost
                };
        }
    }
    aggregate(method, groupTotals, discount) {
        switch(method.type){
            case 'sum':
                return groupTotals.reduce((acc, t)=>acc + t, 0);
            case 'sum_with_discount':
                {
                    const sum = groupTotals.reduce((acc, t)=>acc + t, 0);
                    return sum - (discount ?? 0);
                }
            case 'tier_rollup':
                return groupTotals.reduce((acc, t)=>acc + t, 0);
            case 'pass_through':
                return groupTotals[0] ?? 0;
            case 'subcontractor_pass_through':
                return groupTotals.reduce((acc, t)=>acc + t, 0);
        }
    }
    // ── CLIN Evaluation ───────────────────────────────────────────────────
    evaluateCLIN(input, rateCard, globals) {
        let clin = input;
        const retainedStructure = clin.laborStructure;
        const removed = new Set(rateCard.categories.filter((c)=>c.removedFromSectionB).flatMap((c)=>[
                c.laborCategoryId,
                c.lcatLogicalKey
            ].filter((k)=>!!k)));
        const filterGroup = (g)=>({
                ...g,
                lines: g.lines.filter((l)=>!removed.has(l.laborCategoryId ?? '') && !removed.has(l.lcatLogicalKey ?? ''))
            });
        const structure = clin.laborStructure;
        if (structure.type === 'direct' || structure.type === 'government_defined') {
            clin = {
                ...clin,
                laborStructure: {
                    ...structure,
                    groups: structure.groups.map(filterGroup)
                }
            };
        } else if (structure.type === 'tiered') {
            clin = {
                ...clin,
                laborStructure: {
                    ...structure,
                    tiers: structure.tiers.map((t)=>({
                            ...t,
                            group: filterGroup(t.group)
                        }))
                }
            };
        } else if (structure.type === 'iteration_based') {
            clin = {
                ...clin,
                laborStructure: {
                    ...structure,
                    laborBackup: filterGroup(structure.laborBackup)
                }
            };
        }
        if (clin.metadata.removedByAmendment) {
            return {
                ...clin,
                laborStructure: retainedStructure,
                periodTotals: {},
                grandTotal: 0
            };
        }
        const feeMethod = clin.feeMethod.type !== 'none' ? clin.feeMethod : globals.feeMethod;
        const periodTotals = {};
        let grandTotal = 0;
        for (const period of clin.periods){
            const periodResult = this.evaluateCLINForPeriod(clin, rateCard, period, feeMethod ?? {
                type: 'none'
            }, globals.indirectConfig);
            periodTotals[period] = periodResult;
            grandTotal += periodResult.totalCost;
        }
        return {
            ...clin,
            laborStructure: retainedStructure,
            periodTotals,
            grandTotal
        };
    }
    evaluateCLINForPeriod(clin, rateCard, period, feeMethod, indirectConfig) {
        const rateLookup = this.buildRateLookup(rateCard, clin.rateMethod, period);
        const laborResult = this.evaluateLaborStructure(clin.laborStructure, rateLookup, period);
        const laborCost = this.aggregate(clin.aggregation, laborResult.groupTotals, laborResult.discount);
        // Compute indirect costs
        let indirectCost = 0;
        if (clin.rateMethod.type === 'bare_plus_wrap') {
            const wrapRate = this.computeWrapRateFromConfig(clin.rateMethod.wrapRateConfig, 'labor');
            indirectCost = laborCost * (wrapRate - 1);
        } else if (indirectConfig) {
            const wrapRate = this.computeWrapRateFromConfig(indirectConfig, 'labor');
            indirectCost = laborCost * (wrapRate - 1);
        }
        const costBeforeFee = laborCost + indirectCost;
        // Apply fee
        const { feeAmount, totalWithFee } = this.computeFee(feeMethod, costBeforeFee);
        // Derive unit price if volume method supports it
        let unitPrice;
        if (clin.volumeMethod.type === 'exercise_based') {
            const months = clin.volumeMethod.monthsPerPeriod[period] ?? 12;
            unitPrice = months > 0 ? costBeforeFee / months : undefined;
        }
        return {
            laborCost,
            nonLaborCost: 0,
            indirectCost,
            feeCost: feeAmount,
            totalCost: totalWithFee,
            unitPrice
        };
    }
    evaluateLaborStructure(structure, rateLookup, period) {
        switch(structure.type){
            case 'direct':
            case 'government_defined':
                {
                    const groupTotals = [];
                    for (const group of structure.groups){
                        const result = this.evaluateGroup(group, {}, period, rateLookup);
                        groupTotals.push(result.total);
                    }
                    return {
                        groupTotals
                    };
                }
            case 'tiered':
                {
                    const groupTotals = [];
                    for (const tier of structure.tiers){
                        const result = this.evaluateGroup(tier.group, {}, period, rateLookup);
                        if (tier.exerciseCount) {
                            const exercises = tier.exerciseCount.value ?? 0;
                            groupTotals.push(result.total * exercises);
                        } else {
                            groupTotals.push(result.total);
                        }
                    }
                    return {
                        groupTotals
                    };
                }
            case 'iteration_based':
                {
                    const result = this.evaluateGroup(structure.laborBackup, {}, period, rateLookup);
                    const iterations = structure.iterationsPerPeriod[period] ?? 0;
                    return {
                        groupTotals: [
                            result.total * iterations
                        ]
                    };
                }
            case 'tiered_shared':
                // Should be resolved before reaching here, but handle gracefully
                return {
                    groupTotals: [
                        0
                    ]
                };
            case 'none':
                return {
                    groupTotals: [
                        0
                    ]
                };
        }
    }
    buildRateLookup(rateCard, rateMethod, period, location) {
        const lookup = {};
        for (const cat of rateCard.categories){
            if (cat.removedFromSectionB) continue;
            // Try composite key (period.location), then period, then 'default', then first available
            let rateParam = null;
            if (location) {
                rateParam = cat.rates[`${period}.${location}`];
            }
            if (!rateParam) {
                rateParam = cat.rates[period] ?? (period === 'default' ? cat.rates.base : undefined) ?? cat.rates.default ?? Object.values(cat.rates)[0];
            }
            const bareRate = rateParam?.value ?? 0;
            lookup[cat.laborCategoryId] = this.applyRateMethod(rateMethod, bareRate, cat.laborCategoryId, period);
        }
        return lookup;
    }
    // ── Scenario Evaluation ───────────────────────────────────────────────
    evaluateScenario(pricing) {
        if (!pricing.clinPricing || !pricing.rateCard) {
            return pricing;
        }
        const globals = {
            feeMethod: pricing.globalFeeMethod,
            indirectConfig: pricing.globalIndirectConfig
        };
        const sharedConfigs = pricing.sharedTierConfigs ?? [];
        const evaluatedCLINs = pricing.clinPricing.map((clin)=>{
            // Resolve tiered_shared → tiered before evaluation
            const resolved = clin.laborStructure.type === 'tiered_shared' && sharedConfigs.length > 0 ? {
                ...clin,
                laborStructure: resolveSharedTiers(clin.laborStructure, sharedConfigs)
            } : clin;
            if (!pricing.rateCard) return resolved;
            return {
                ...this.evaluateCLIN(resolved, pricing.rateCard, globals),
                laborStructure: clin.laborStructure
            };
        });
        return {
            ...pricing,
            clinPricing: evaluatedCLINs
        };
    }
    // ── Helpers ───────────────────────────────────────────────────────────
    getFormulaDeps(formula) {
        switch(formula.type){
            case 'multiply':
                return formula.operands;
            case 'sum':
                return [];
            case 'subtract':
                return [
                    formula.left,
                    formula.right
                ];
            case 'divide':
                return [
                    formula.numerator,
                    formula.denominator
                ];
            case 'percentage':
                return [
                    formula.value,
                    formula.base
                ];
            case 'product_chain':
                return formula.factors;
            case 'wrap_rate':
                return [
                    formula.pools,
                    formula.fee
                ];
            case 'escalate':
                return [
                    formula.base,
                    formula.rate,
                    formula.year
                ];
            case 'clamp':
                return [
                    formula.value,
                    formula.min,
                    formula.max
                ];
            case 'conditional':
                return [
                    formula.condition,
                    formula.ifTrue,
                    formula.ifFalse
                ];
            case 'lookup':
                return [];
            case 'custom':
                return formula.variables;
        }
    }
    computeWrapRateFromConfig(config, costType) {
        const pools = config.pools ?? [];
        const useMultiplicative = config.useMultiplicative ?? true;
        const applicablePools = pools.filter((p)=>p.appliesToCostTypes.includes(costType)).sort((a, b)=>a.applyOrder - b.applyOrder);
        if (useMultiplicative) {
            let rate = 1.0;
            for (const pool of applicablePools){
                rate *= 1 + pool.ratePercent;
            }
            return rate;
        }
        let sum = 1.0;
        for (const pool of applicablePools){
            sum += pool.ratePercent;
        }
        return sum;
    }
    /**
   * Compute a per-pool breakdown for UI display.
   * Returns the indirect cost contribution of each pool.
   */ computeIndirectBreakdown(config, laborCost, costType) {
        const pools = (config.pools ?? []).filter((p)=>p.appliesToCostTypes.includes(costType)).sort((a, b)=>a.applyOrder - b.applyOrder);
        const breakdown = [];
        if (config.useMultiplicative) {
            let base = laborCost;
            for (const pool of pools){
                const amount = base * pool.ratePercent;
                breakdown.push({
                    poolName: pool.poolName,
                    rate: pool.ratePercent,
                    amount
                });
                base += amount; // cascading
            }
        } else {
            for (const pool of pools){
                const amount = laborCost * pool.ratePercent;
                breakdown.push({
                    poolName: pool.poolName,
                    rate: pool.ratePercent,
                    amount
                });
            }
        }
        return breakdown;
    }
}
// ── Rate Card Escalation Utilities ────────────────────────────────────
/**
 * Apply escalation to rate card rates across periods.
 * For each category, computes option period rates as:
 *   baseRate × (1 + escalation%)^periodIndex
 * Uses per-period override if provided, otherwise defaultPct.
 */ export function applyRateCardEscalation(rateCard, basePeriod, defaultPct, perPeriodOverrides) {
    // Collect all periods from rate keys
    const allPeriods = new Set();
    for (const cat of rateCard.categories){
        for (const key of Object.keys(cat.rates)){
            // Strip location suffix if present
            const dotIdx = key.lastIndexOf('.');
            const period = dotIdx > 0 && (key.endsWith('.on_site') || key.endsWith('.off_site')) ? key.substring(0, dotIdx) : key;
            allPeriods.add(period);
        }
    }
    // Order periods: base first, then alphabetically (Base Period, Option 1, Option 2, ...)
    const sortedPeriods = [
        ...allPeriods
    ].sort((a, b)=>{
        if (a === basePeriod) return -1;
        if (b === basePeriod) return 1;
        return a.localeCompare(b);
    });
    const basePeriodIdx = sortedPeriods.indexOf(basePeriod);
    const updatedCategories = rateCard.categories.map((cat)=>{
        const updatedRates = {
            ...cat.rates
        };
        for (const [key, param] of Object.entries(cat.rates)){
            // Determine which period this key belongs to
            const dotIdx = key.lastIndexOf('.');
            const isLocation = dotIdx > 0 && (key.endsWith('.on_site') || key.endsWith('.off_site'));
            const period = isLocation ? key.substring(0, dotIdx) : key;
            const periodIdx = sortedPeriods.indexOf(period);
            if (periodIdx <= basePeriodIdx) continue; // Don't modify base period
            // Get base rate for this category+location
            const baseKey = isLocation ? `${basePeriod}${key.substring(dotIdx)}` : basePeriod;
            const baseRate = cat.rates[baseKey]?.value ?? param.value ?? 0;
            if (baseRate === 0) continue;
            const yearsFromBase = periodIdx - basePeriodIdx;
            const escalationPct = perPeriodOverrides?.[period] ?? defaultPct;
            const escalatedRate = baseRate * (1 + escalationPct) ** yearsFromBase;
            updatedRates[key] = {
                ...param,
                value: Math.round(escalatedRate * 100) / 100
            };
        }
        return {
            ...cat,
            rates: updatedRates
        };
    });
    return {
        ...rateCard,
        categories: updatedCategories
    };
}
